import firebase from "firebase/app";

import {
  EventVideoGroupSchema,
  EventVideoGroupsSetSchema,
  FeatureFlagSchema,
  IssuedTicketSchema,
  TicketRedeemGroupSchema,
} from "./types";

import type { ZodMiniAny, z } from "zod/mini";

/**
 * Zod スキーマを Firebase の FirestoreDataConverter に変換する
 * withConverter で使用可能
 * パース失敗時は null を返す（呼び出し側でフィルタリングが必要）
 */
const zodConverter = <S extends ZodMiniAny, T = z.infer<S>>(
  schema: z.ZodMiniType<T>
): firebase.firestore.FirestoreDataConverter<T | null> => {
  return {
    toFirestore(document: T | null): firebase.firestore.DocumentData {
      if (document === null) {
        return {};
      }
      const result = schema.safeParse(document);
      if (!result.success) {
        console.error("Failed to serialize document:", result.error);
        return {};
      }
      return result.data as firebase.firestore.DocumentData;
    },
    fromFirestore(
      snapshot: firebase.firestore.QueryDocumentSnapshot,
      options: firebase.firestore.SnapshotOptions
    ): T | null {
      const snapshotData = snapshot.data(options);
      snapshotData._id = snapshot.id;
      console.log(
        "check if from cache",
        snapshot.ref.path,
        snapshot.metadata.fromCache
      );
      const result = schema.safeParse({
        _id: snapshot.id,
        _refPath: snapshot.ref.path,
        ...snapshotData,
      });
      if (!result.success) {
        console.error(`Failed to parse document ${snapshot.id}:`, result.error);
        return null;
      }
      return result.data;
    },
  };
};

const dataPoint = <S extends ZodMiniAny, T = z.infer<S>>(
  collectionPath: string,
  schema: z.ZodMiniType<T>
) =>
  firebase
    .firestore()
    .collection(collectionPath)
    .withConverter(zodConverter(schema));

/**
 * doc 用の型付きconverter
 * 現在未使用のため_つき
 */
const _dataPointDoc = <S extends ZodMiniAny, T = z.infer<S>>(
  collectionPath: string,
  schema: z.ZodMiniType<T>
) =>
  firebase.firestore().doc(collectionPath).withConverter(zodConverter(schema));

/**
 * collection Group 用の型付きconverter
 * 現在未使用のため_つき
 */
const _dataPointCollectionGroup = <S extends ZodMiniAny, T = z.infer<S>>(
  collectionPath: string,
  schema: z.ZodMiniType<T>
) =>
  firebase
    .firestore()
    .collectionGroup(collectionPath)
    .withConverter(zodConverter(schema));

/**
 * DB アクセス用オブジェクト
 * ここにFirestoreのパスとDBの型定義をマッピングさせる
 */
export const dbFirestore = {
  videoGroups: () => dataPoint("videoGroups", EventVideoGroupSchema),
  videoGroupsSets: () =>
    dataPoint("videoGroupsSets", EventVideoGroupsSetSchema),
  featureFlags: () => dataPoint("featureFlags", FeatureFlagSchema),
  ticketRedeemGroups: () =>
    dataPoint("ticketRedeemGroups", TicketRedeemGroupSchema),
  issuedTickets: (eventId: string) =>
    dataPoint(`tickets/${eventId}/issuedTickets`, IssuedTicketSchema),
};
